import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { CallButton } from '../../components/call';

export default function About() {
  const { effectiveTheme, setTheme } = useTheme();

  return (
    <div className="min-h-screen bg-surface-light dark:bg-surface-dark">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-surface-darker/90 backdrop-blur-sm border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-2xl">
              🕺🏻
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setTheme(effectiveTheme === 'dark' ? 'light' : 'dark')}
                className="p-2 rounded-soft text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                aria-label="Toggle theme"
              >
                {effectiveTheme === 'dark' ? <SunIcon /> : <MoonIcon />}
              </button>
              <Link to="/login" className="btn-primary text-sm py-2">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-medium mb-6">
              <ShieldIcon />
              CISSP + HCISPP Certified
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-neutral-100 mb-6 leading-tight">
              Wez Ireland
            </h1>
            <p className="text-xl sm:text-2xl text-primary-600 dark:text-primary-400 font-medium mb-4">
              Healthcare CISO | Digital Health Security Leader
            </p>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto readable-text">
              10+ years building security programs from zero to certified — SOC 2, HIPAA, HITRUST, and
              FDA medical device pathways. Fractional CISO serving digital health and SaaS companies.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <CallButton className="btn-primary">
              <PhoneIcon />
              Call Me
            </CallButton>
            <a
              href="https://linkedin.com/in/wezireland"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              <LinkedInIcon />
              LinkedIn
            </a>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            <StatCard value="15+" label="Years Experience" />
            <StatCard value="6+" label="Healthcare Orgs" />
            <StatCard value="SOC 2" label="HIPAA & HITRUST" />
            <StatCard value="3" label="Languages" />
          </div>
        </div>
      </section>

      {/* Current Role */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-surface-darker">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-8 text-center">
            Current Focus
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <RoleCard
              company="EvoTechnica"
              role="Fractional CISO"
              period="Aug 2023 - Present"
              description="Fractional CISO and security consultant serving digital health and SaaS companies. Build compliance programs and security operating models from first principles — HITRUST, SOC 2, ISO 27001, HIPAA, and AI/agentic risk governance."
              highlight
            />
            <RoleCard
              company="Wisp"
              role="Information Security Consultant"
              period="Apr 2024 - Mar 2026"
              description="Fractional security leadership for a telehealth organization. Achieved HIPAA certification and led SOC 2 Type II readiness — full control infrastructure complete. Advised on AI tool governance, GenAI risk posture, identity modernization (Okta SSO, RBAC), endpoint/BYOD security, and GRC operations maturity in Drata."
            />
          </div>
        </div>
      </section>

      {/* Skills & Specialties */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-8 text-center">
            Expertise
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <SkillCard
              icon={<ShieldIcon />}
              title="Security & Compliance"
              skills={['SOC 2 Type I/II', 'HIPAA', 'HITRUST', 'ISO 27001', 'NIST CSF', 'Zero Trust']}
            />
            <SkillCard
              icon={<CloudIcon />}
              title="Cloud & Identity"
              skills={['Google Workspace', 'Okta', 'Azure AD', 'JAMF', 'MDM', 'SSO/SAML']}
            />
            <SkillCard
              icon={<CodeIcon />}
              title="DevSecOps & AI Security"
              skills={['CI/CD Security', 'SDLC Integration', 'AI/GenAI Governance', 'Agentic AI Risk', 'GRC Automation']}
            />
            <SkillCard
              icon={<HeartIcon />}
              title="Healthcare Focus"
              skills={['Telehealth', 'PHI Protection', 'BAA Management', 'Medical Device Security', 'FDA Approval', 'TPRM']}
            />
            <SkillCard
              icon={<UsersIcon />}
              title="Leadership"
              skills={['Team Training', 'Stakeholder Comms', 'Vendor Management', 'Incident Response']}
            />
            <SkillCard
              icon={<GlobeIcon />}
              title="Global Experience"
              skills={['US & UK Citizen', 'Canada Work Permit', 'Remote-First', 'Multi-Cultural']}
            />
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-surface-darker">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-8 text-center">
            Career Highlights
          </h2>

          <div className="space-y-6">
            <TimelineItem
              company="EasyHealth"
              role="Chief Information Security Officer"
              period="2025"
              highlights={[
                'Hired to build SOC 2 and HIPAA readiness operating model from the ground up',
                'Established governance, control ownership, evidence infrastructure, and audit tooling',
                'Materially improved security posture across identity, endpoint, vendor risk, and incident response'
              ]}
            />
            <TimelineItem
              company="Waymark"
              role="Information Security Consultant"
              period="2022 - 2023"
              highlights={[
                'Achieved SOC 2 Type I certification and HIPAA attestation',
                'Built complete policy, procedure, and control foundation for SOC 2 Type II — all controls in place before tenure ended',
                'Built ISO 42001-aligned AI governance P&Ps and guardrails — pre-standard AI risk management framework'
              ]}
            />
            <TimelineItem
              company="Overjet"
              role="Information Security Consultant"
              period="2021 - 2022"
              highlights={[
                'Achieved HITRUST and ISO 13485 certification enabling the FDA clearance pathway',
                'Secured AI/ML dental diagnostic software for FDA medical device submission',
                'Implemented cybersecurity controls supporting Overjet\'s FDA approval for AI-powered imaging'
              ]}
            />
            <TimelineItem
              company="Doctor On Demand"
              role="Security Leadership (6 years)"
              period="2015 - 2021"
              highlights={[
                'Achieved HITRUST certification and SOC 2 Type II (controls fully inheritable from HITRUST — the more demanding framework)',
                'Grew from Operations Engineer to Security Architect over 6 years',
                'M&A security due diligence for Grand Rounds merger'
              ]}
            />
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-8 text-center">
            Certifications & Education
          </h2>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="font-semibold text-lg mb-4 text-neutral-900 dark:text-neutral-100">
                Certifications
              </h3>
              <ul className="space-y-3">
                <CertItem name="CISSP" desc="Certified Information Systems Security Professional" />
                <CertItem name="HCISPP" desc="HealthCare Information Security and Privacy Practitioner — rare dual credential, one of ~3,500 active holders globally" />
                <CertItem name="MCSE" desc="Microsoft Certified Solutions Expert: Communication" />
                <CertItem name="Full-Stack Developer" desc="Web and Mobile Development" />
              </ul>
            </div>

            <div className="card">
              <h3 className="font-semibold text-lg mb-4 text-neutral-900 dark:text-neutral-100">
                Education
              </h3>
              <ul className="space-y-3">
                <EduItem
                  school="Langara College"
                  degree="MCA, Web and Mobile App Design, Development & Security"
                  year="2020 - 2021"
                />
                <EduItem
                  school="Southern New Hampshire University"
                  degree="BA, Photography"
                  year="2017 - 2020"
                />
                <EduItem
                  school="Kennesaw State University"
                  degree="BS, Computer Science"
                  year="2003 - 2006"
                />
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Let's Work Together
          </h2>
          <p className="text-primary-100 mb-8">
            Looking for a fractional CISO or security consulting? I specialize in building healthcare
            security programs from zero to certified — SOC 2, HIPAA, HITRUST, and FDA medical device pathways.
            Remote-first, US + Canada eligible.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <CallButton className="btn bg-white text-primary-700 hover:bg-primary-50">
              <PhoneIcon />
              Call Me Directly
            </CallButton>
            <a
              href="https://linkedin.com/in/wezireland"
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-white/20 text-white hover:bg-white/30"
            >
              <LinkedInIcon />
              Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-neutral-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-neutral-500 dark:text-neutral-400">
          <p>&copy; {new Date().getFullYear()} Wez Ireland</p>
          <a
            href="https://linkedin.com/in/wezireland"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary-600 dark:hover:text-primary-400"
          >
            LinkedIn
          </a>
        </div>
      </footer>
    </div>
  );
}

// Components

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="card text-center">
      <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400">
        {value}
      </div>
      <div className="text-sm text-neutral-500 dark:text-neutral-400">{label}</div>
    </div>
  );
}

function RoleCard({
  company,
  role,
  period,
  description,
  highlight,
}: {
  company: string;
  role: string;
  period: string;
  description: string;
  highlight?: boolean;
}) {
  return (
    <div className={`card ${highlight ? 'ring-2 ring-primary-500' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-lg text-neutral-900 dark:text-neutral-100">{company}</h3>
          <p className="text-primary-600 dark:text-primary-400">{role}</p>
        </div>
        {highlight && (
          <span className="badge-primary text-xs">Current</span>
        )}
      </div>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">{period}</p>
      <p className="text-sm text-neutral-600 dark:text-neutral-300 readable-text">{description}</p>
    </div>
  );
}

function SkillCard({
  icon,
  title,
  skills,
}: {
  icon: React.ReactNode;
  title: string;
  skills: string[];
}) {
  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-soft bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400">
          {icon}
        </div>
        <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {skills.map((skill) => (
          <span
            key={skill}
            className="px-2 py-1 text-xs rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
          >
            {skill}
          </span>
        ))}
      </div>
    </div>
  );
}

function TimelineItem({
  company,
  role,
  period,
  highlights,
}: {
  company: string;
  role: string;
  period: string;
  highlights: string[];
}) {
  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-lg text-neutral-900 dark:text-neutral-100">{company}</h3>
          <p className="text-primary-600 dark:text-primary-400">{role}</p>
        </div>
        <span className="text-sm text-neutral-500 dark:text-neutral-400">{period}</span>
      </div>
      <ul className="space-y-2">
        {highlights.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-300">
            <span className="text-primary-500 mt-1">-</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CertItem({ name, desc }: { name: string; desc: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="text-primary-500 mt-1">
        <CheckIcon />
      </span>
      <div>
        <span className="font-medium text-neutral-900 dark:text-neutral-100">{name}</span>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{desc}</p>
      </div>
    </li>
  );
}

function EduItem({ school, degree, year }: { school: string; degree: string; year: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="text-primary-500 mt-1">
        <GradIcon />
      </span>
      <div>
        <span className="font-medium text-neutral-900 dark:text-neutral-100">{school}</span>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{degree}</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500">{year}</p>
      </div>
    </li>
  );
}

// Icons

function SunIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function CloudIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
    </svg>
  );
}

function CodeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function GradIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M12 14l9-5-9-5-9 5 9 5z" />
      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
  );
}
